import { CapacitorSQLite, SQLiteConnection } from '@capacitor-community/sqlite';
import { Capacitor } from '@capacitor/core';

/**
 * Servicio encargado de gestionar la base de datos local SQLite en dispositivos nativos.
 * Proporciona métodos para inicializar la base de datos, ejecutar consultas y sincronizar con el servidor.
 */
class SqliteService {
    constructor() {
        this.sqlite = null;
        this.db = null;
        this.isNative = Capacitor.isNativePlatform();
    }

    /**
     * Inicializa la conexión con SQLite y crea las tablas necesarias si no existen.
     */
    async init() {
        if (!this.isNative) return;

        try {
            const sqlite = new SQLiteConnection(CapacitorSQLite);
            this.sqlite = sqlite;

            // Crea o abre la base de datos 'comprara'
            this.db = await sqlite.createConnection(
                'comprara',
                false,
                'no-encryption',
                1,
                false
            );

            await this.db.open();

            // Crea la tabla de listas offline si no existe
            const createTableQuery = `
                CREATE TABLE IF NOT EXISTS listas_offline (
                    id TEXT PRIMARY KEY,
                    nombre TEXT NOT NULL,
                    items TEXT,
                    sync_status INTEGER DEFAULT 0,
                    created_at DATETIME DEFAULT CURRENT_TIMESTAMP
                );
            `;
            // Crea la tabla de tickets pendientes de sincronizar
            const createTicketsTable = `
                CREATE TABLE IF NOT EXISTS tickets_offline (
                    id TEXT PRIMARY KEY,
                    imagen_path TEXT,
                    sync_status INTEGER DEFAULT 0,
                    created_at DATETIME DEFAULT CURRENT_TIMESTAMP
                );
            `;
            await this.db.execute(createTableQuery);
            await this.db.execute(createTicketsTable);
            console.log('SQLite inicializado correctamente');
        } catch (error) {
            console.error('Error inicializando SQLite:', error);
        }
    }

    /**
     * Guarda una lista en la base de datos local para acceso offline.
     * @param {string} id Identificador único de la lista.
     * @param {string} nombre Nombre de la lista.
     * @param {object} items Contenido de la lista en formato JSON.
     */
    async saveListLocally(id, nombre, items) {
        if (!this.db) return;

        const query = `
            INSERT OR REPLACE INTO listas_offline (id, nombre, items, sync_status)
            VALUES (?, ?, ?, 0);
        `;
        const values = [id, nombre, JSON.stringify(items)];
        await this.db.run(query, values);
    }

    /**
     * Recupera todas las listas almacenadas localmente.
     * @returns {Array} Lista de objetos de listas locales.
     */
    async getLocalLists() {
        if (!this.db) return [];

        const query = "SELECT * FROM listas_offline ORDER BY created_at DESC;";
        const result = await this.db.query(query);
        return result.values.map(list => ({
            ...list,
            items: JSON.parse(list.items)
        }));
    }

    /**
     * Guarda un ticket para procesar después (escaneo OCR offline).
     * @param {string} id ID temporal o definitivo del ticket.
     * @param {string} path Ruta local de la imagen en el dispositivo.
     */
    async saveTicketOffline(id, path) {
        if (!this.db) return;
        const query = "INSERT INTO tickets_offline (id, imagen_path, sync_status) VALUES (?, ?, 0);";
        await this.db.run(query, [id, path]);
    }

    /**
     * Obtiene los tickets que aún no se han subido al servidor.
     */
    async getPendingTickets() {
        if (!this.db) return [];
        const result = await this.db.query("SELECT * FROM tickets_offline WHERE sync_status = 0;");
        return result.values || [];
    }

    /**
     * Marca una lista como sincronizada con el servidor.
     * @param {string} id Identificador de la lista.
     */
    async markAsSynced(id) {
        if (!this.db) return;
        const query = "UPDATE listas_offline SET sync_status = 1 WHERE id = ?;";
        await this.db.run(query, [id]);
    }

    /**
     * Marca un ticket como sincronizado.
     * @param {string} id Identificador del ticket.
     */
    async markTicketAsSynced(id) {
        if (!this.db) return;
        const query = "UPDATE tickets_offline SET sync_status = 1 WHERE id = ?;";
        await this.db.run(query, [id]);
    }
}

export default new SqliteService();
